<template>
  <div class="min-h-screen">
    <template v-if="isMounted">
      <StickyHeader
        :data="headerData"
        :activeIndex="activeTab"
        @user-action:tab-switch="handleTabSwitch"
        @user-action:button-click="handleButtonClick"
      />

      <TransitionGroup name="menu">
        <article v-if="activeTab === 0" :key="1" class="-mt-40 bg-light-gray lg:-mt-52">
          <ProgramProductBlocks
            v-if="data.programProduct?.blocks"
            class="pt-40"
            :data="data.programProduct.blocks"
          />
        </article>

        <article
          v-if="activeTab === 1 && data.programProduct?.displayTehnicalDetails"
          :key="2"
          class="bg-neutral-100 py-10 lg:pb-28"
        >
          <ProgramProductDetailHero
            v-if="data.programProduct?.productDetailHero[0]"
            :data="data.programProduct.productDetailHero[0]"
          />
          <ProgramProductDetails
            v-if="data.programProduct.body?.blocks"
            :data="data.programProduct?.body.blocks"
          />
        </article>
      </TransitionGroup>
    </template>
  </div>
</template>

<script setup lang="ts">
import { toHead } from 'vue-datocms'
import { useAppStore } from '~/stores/AppStore'
import { useInquiryStore } from '~/stores/InquiryStore'
import { ModelApiKey } from '~/types/models'
import { ProgramProductNavigation } from '~/components/StickyHeader.vue'
import { StringMultiLocaleField, BooleanTypeMultiLocaleField } from '~/types/generated'

const { locale, t } = useI18n()
const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const store = useAppStore()
const inquiryStore = useInquiryStore()
const activeTab = ref(0)
const isMounted = ref(false)

const { throwError } = useNuxtError()

const { data, error } = await useAsyncGql('programProduct', {
  // @ts-ignore
  locale,
  slug: route.params.slug as string,
  options: {
    initialCache: false
  }
})

if (!data?.value!.programProduct || error.value) {
  throwError(404)
}

const headerData = computed<ProgramProductNavigation>(() => {
  return {
    productCategory: data.value.programProduct.category.programCategory,
    productTitle: data.value.programProduct.product,
    navigation: [
      {
        title: t('programProduct.aboutProduct'),
        visible: true
      },
      {
        title: t('programProduct.productDetail'),
        visible: data.value.programProduct?.displayTehnicalDetails
      },
      {
        title: `${t('programProduct.catalog')} (PDF)`,
        slug: data.value.programProduct?.pdfLink,
        description: `${data.value.programProduct?.product}.pdf`,
        visible: true
      }
    ],
    button: {
      text: t('programProduct.buttonText')
    }
  }
})

if (route.query.tab === headerData.value.navigation[1].title) {
  activeTab.value = 1
} else if (route.query.tab && route.query.tab !== headerData.value.navigation[1].title) {
  throwError(404)
}

if (data?.value!.programProduct) {
  route.meta.nuxtI18n = useLocaleSlugs(
    data.value.programProduct._allSlugLocales as StringMultiLocaleField[],
    data.value.programProduct._allPublishTranslationLocales as BooleanTypeMultiLocaleField[]
  )
}

const datoHead = toHead((data.value as any).programProduct.seo)
const i18nHeadLinks = useLocaleHeadLinks(ModelApiKey.ProgramProduct)

function handleTabSwitch(index: number, navTitle: string) {
  activeTab.value = index

  if (index === 1) {
    router.replace({ query: { tab: navTitle } })
  } else {
    router.replace({ query: undefined })
  }
}

function handleButtonClick() {
  inquiryStore.skipToStep(data.value?.programProduct?.category, data.value.programProduct)
}

useHead(() => {
  const metaTags = [...(datoHead.meta || [])]

  // Check for noindex
  if ((data.value as any).programProduct.excludeRecordFromSearchEngines) {
    metaTags.push({ name: 'robots', hid: 'robots', content: 'noindex' })
  }

  return {
    title: datoHead.title,
    link: [...i18nHeadLinks],
    meta: [
      {
        hid: 'og:url',
        property: 'og:url',
        content: config.public.appUrl + route.fullPath
      },
      ...metaTags
    ]
  }
})

onMounted(() => {
  isMounted.value = true
  store.setTheme('gray')
})
</script>
